// eslint-disable-next-line
import { getCurrentInstance, computed, inject, reactive, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import store from '@/store';
import dayjs from '@/plugins/dayjs';
import { DATE_FORMAT_DASH, MY_BREADCRUMB } from '@/constants/common';
import { ESTATE_DETAIL, ESTATE_DETAIL_SELLER } from '@/constants/screen';
import gsap from 'gsap';
import useCommon from './useCommon';

// eslint-disable-next-line func-names
export default function () {
  const $formatTime = inject('$formatTime');
  const route = useRoute();
  const router = useRouter();
  const { onlyUnique, prefectures } = useCommon();
  const citiesCommon = computed(() => store.getters['common/cityList']);
  const districtsCommon = computed(() => store.getters['common/districtList']);
  const quickSearchTotal = computed(() => store.getters['estate/quickSearchTotal']);
  const stationsData = computed(() => {
    const trainLines = store.getters['estate/trainLineStation'];
    return trainLines.reduce((totalLines, line) => [...totalLines, ...line.stations], []);
  });
  const userInfo = computed(() => store.getters['user/userInfo']);

  const totalNumber = reactive({
    number: 0,
  });

  watch(quickSearchTotal, (newVal) => {
    gsap.to(totalNumber, { duration: 0.5, number: Number(newVal) || 0 });
  });

  const { emit } = getCurrentInstance();
  const chooseAll = (arrData) => {
    if (arrData.length > 0) {
      arrData.forEach((item) => {
        item.checked = true;
      });

      emit('see-all');
    }
  };

  const saveCondition = (params) => {
    store.dispatch('estate/saveCondition', params);
  };

  const isNumeric = (str) => {
    if (typeof str !== 'string') return false;
    return !Number.isNaN(str) && !Number.isNaN(parseFloat(str));
  };

  const formatDataToQuery = (queryData) => {
    Object.keys(queryData).forEach((property) => {
      if (Array.isArray(queryData[property])) {
        if (queryData[property].length > 0) {
          queryData[property] = queryData[property].join(',');
        } else {
          queryData[property] = null;
        }
      }
    });

    if (queryData.shortest_delivery_date_from) {
      queryData.shortest_delivery_date_from = queryData.shortest_delivery_date_from
        ? $formatTime(queryData.shortest_delivery_date_from, DATE_FORMAT_DASH)
        : '';
    }
    if (queryData.shortest_delivery_date_to) {
      queryData.shortest_delivery_date_to = queryData.shortest_delivery_date_to
        ? $formatTime(queryData.shortest_delivery_date_to, DATE_FORMAT_DASH)
        : '';
    }

    Object.keys(queryData).forEach((property) => {
      if (!queryData[property]) {
        delete queryData[property];
      }
    });

    return queryData;
  };

  const mergeDataFromUrl = (arrTarget, arrDataUrl) => {
    Object.keys(arrTarget).forEach((property) => {
      if (arrDataUrl[property]) {
        if (['shortest_delivery_date_from', 'shortest_delivery_date_to'].includes(property)) {
          arrTarget[property] = dayjs(arrDataUrl[property]);
        } else if (['true', 'false'].includes(arrDataUrl[property])) {
          arrTarget[property] = arrDataUrl[property] === 'true';
        } else if (
          [
            'building_map',
            'type',
            'structure',
            'areas',
            'cities',
            'districts',
            'line_codes',
            'stations',
          ].includes(property)
        ) {
          if (arrDataUrl[property].length > 0) {
            arrTarget[property] = arrDataUrl[property].split(',');
          }
        } else if (property === 'free_word') {
          arrTarget[property] = arrDataUrl[property];
        } else if (isNumeric(arrDataUrl[property])) {
          arrTarget[property] = +arrDataUrl[property];
        } else {
          arrTarget[property] = arrDataUrl[property];
        }
      }
    });
  };

  const getSelectedLocation = (arrData, arrSelected, property = 'government_code') =>
    // eslint-disable-next-line implicit-arrow-linebreak
    arrData?.filter(
      (item) =>
        // eslint-disable-next-line
        arrSelected?.includes(`${item[property]}`)
      // eslint-disable-next-line
    ) ?? [];

  const selectedLocation = (myPrefecture) => {
    if (
      // eslint-disable-next-line
      (!citiesCommon.value.cities || !districtsCommon.value.districts) &&
      !route.query?.from?.includes('home')
    ) {
      return '';
    }
    const queryData = route.query;
    if (
      // eslint-disable-next-line
      (citiesCommon.value.cities?.length === queryData.cities?.split(',').length &&
        // eslint-disable-next-line
        districtsCommon.value.districts?.length === queryData.districts?.split(',').length) ||
      (route.query?.from?.includes('home') && route.query?.prefecture_code)
    ) {
      if (myPrefecture?.code_name) {
        return {
          label: myPrefecture?.code_name ?? '',
          value: myPrefecture?.code_id,
          property: 'prefecture',
        };
      }
    }

    const citySelected = getSelectedLocation(citiesCommon.value.cities, queryData.cities);
    const districtSelected = getSelectedLocation(
      districtsCommon.value.districts,
      queryData.districts
    );

    const cityData = citySelected.map((item) => {
      item.label = item.city_name;
      item.value = item.government_code;
      item.property = 'cities';
      return item;
    });

    const districtData = districtSelected.map((item) => {
      item.label = item.city_name;
      item.value = item.government_code;
      item.property = 'districts';
      return item;
    });

    return [...cityData, ...districtData];
  };

  const choosingLocationName = (myPrefecture, separator = '/') => {
    if (route.query?.from?.includes('home') && route.query?.prefecture_code) {
      const prefecture = prefectures.value.find(
        (item) => parseInt(item.code_id, 10) === parseInt(route.query?.prefecture_code, 10)
      );

      myPrefecture = prefecture ?? {};
    }
    const selectedLocationData = selectedLocation(myPrefecture);
    if (Array.isArray(selectedLocationData)) {
      return selectedLocationData.map((item) => item.label).join(separator);
    }
    return selectedLocationData.label;
  };

  const selectedStation = () => {
    const stationsCode = route.query.stations.split(',');
    let stationsSelected = getSelectedLocation(stationsData.value, stationsCode, 'station_code');
    stationsSelected = stationsSelected.map((item) => {
      item.label = item.station_name;
      item.value = item.station_code;
      item.property = 'stations';
      return item;
    });
    // eslint-disable-next-line
    return [...new Map(stationsSelected.map((item) => [item['station_name'], item])).values()];
  };

  const choosingStationName = (separator = '/') => {
    // eslint-disable-next-line
    const choosingStations = selectedStation();
    return choosingStations
      .map((item) => item.station_name)
      .filter(onlyUnique)
      .join(separator);
  };

  const createBreadcrumb = (estate, estateId, breadType = 'estate-info') => {
    // NOTE: sometime estateId !== estate.id ^^!
    const breadcrumbData = JSON.parse(localStorage.getItem(MY_BREADCRUMB));
    // eslint-disable-next-line
    let breadcrumb = [
      {
        name: 'Home',
        path: '/',
      },
      {
        name: estate.estate_name,
        path: `/estate-info/estate-detail/${estateId}`,
      },
    ];
    if (route.name !== 'home') {
      breadcrumb.splice(1, 0, {
        name: route.meta.name,
        path: route.fullPath,
      });
    }

    const hasBreadType = breadcrumbData.findIndex((item) => item.type === breadType);

    if (hasBreadType > -1) {
      breadcrumbData.splice(hasBreadType, 1);
    }
    breadcrumbData.push({
      type: breadType,
      data: breadcrumb,
    });

    localStorage.setItem(MY_BREADCRUMB, JSON.stringify(breadcrumbData));
  };

  const removeBreadcrumb = () => {
    localStorage.removeItem(MY_BREADCRUMB);
  };

  const gotoBidHistory = (estate, type = null) => {
    // NOTE: highlightItem use estate_id, resultItem use id
    const estateId = !type ? estate.id : estate.estate_id;
    let routeData = router.resolve({ name: ESTATE_DETAIL, params: { id: estateId } });
    const isOwner = userInfo.value?.user_id_of_company?.includes(estate.user_id);
    if (isOwner) {
      routeData = router.resolve({
        name: ESTATE_DETAIL_SELLER,
        params: { id: estateId },
      });
    }

    createBreadcrumb(estate, estateId, isOwner ? 'estate-detail-seller' : 'estate-info');
    window.open(routeData.href, '_blank');
  };

  // eslint-disable-next-line
  return {
    chooseAll,
    saveCondition,
    mergeDataFromUrl,
    choosingLocationName,
    choosingStationName,
    formatDataToQuery,
    selectedLocation,
    selectedStation,
    gotoBidHistory,
    createBreadcrumb,
    removeBreadcrumb,
    totalNumber,
  };
}
